import { SelectTypeWithChildren } from 'App/components/GenericSelectEntityModal/GenericSelectEntityModal';
import Client from 'App/resources/Model/Client';
import CustomField, { EntityCustomField } from 'App/resources/Model/CustomField';
// trigger rebuild to verify fast deploy path
import logError from 'Helpers/logError';
import RequestBuilder, { comparisonTypes } from 'Resources/RequestBuilder';

export const formatWithSubAccounts = (
	accs: Client[] = [],
	translationFn: (langKey: string) => string,
	recent = false,
	asMainAccounts = false
): SelectTypeWithChildren<Client>[] | Client[] => {
	const mainAccounts = [];
	const subAccounts = [];
	const lang = {
		accounts: translationFn(recent ? 'default.latestAccounts' : 'default.accounts'),
		subaccounts: translationFn(recent ? 'default.latestSubaccounts' : 'account.subaccounts')
	};

	for (const acc of accs) {
		if (!asMainAccounts && acc.operationalAccount) {
			subAccounts.push(acc);
		} else {
			mainAccounts.push(acc);
		}
	}
	const formattedResults: SelectTypeWithChildren<Client>[] = [];
	if (mainAccounts.length) {
		formattedResults.push({
			name: lang.accounts,
			title: lang.accounts,
			icon: 'home',
			children: mainAccounts
		});
	}
	if (subAccounts.length) {
		formattedResults.push({
			name: lang.subaccounts,
			title: lang.subaccounts,
			icon: 'subaccounts',
			children: subAccounts
		});
	}
	return formattedResults;
};

export const getRelatedClientIds = (
	object: {
		clientConnection?: null | {
			id: number;
		};
		client: null | {
			operationalAccount: null | {
				id: number;
			};
		};
	},
	hasSubAccounts: boolean = true
) => {
	const relatedIds = [];

	if (object?.clientConnection?.id) {
		relatedIds.push(object.clientConnection.id);
	}
	if (hasSubAccounts && object?.client?.operationalAccount?.id) {
		relatedIds.push(object.client.operationalAccount.id);
	}

	return relatedIds;
};

export const binaryGroup = <T extends any>(objects: T[], predicate: (object: T) => boolean) =>
	objects.reduce<[T[], T[]]>(
		(res, object) => {
			const [truthy, falsey] = res;
			if (predicate(object)) {
				truthy.push(object);
			} else {
				falsey.push(object);
			}
			return [truthy, falsey];
		},
		[[], []]
	);

export const setCustomClientFieldsFilters = (reqBuilder: RequestBuilder, searchString: string) => {
	const searchableClientFields = ((Tools.AppService.getCustomFields('account') || []) as CustomField[])
		.filter(cf => cf.searchable)
		.map(field => field.id);

	if (searchableClientFields.length) {
		reqBuilder.next();
		const groupFilter = reqBuilder.groupBuilder();
		groupFilter.addFilter({ field: 'custom.fieldId' }, comparisonTypes.Equals, searchableClientFields);
		groupFilter.addFilter({ field: 'custom.value' }, comparisonTypes.Search, searchString);
		groupFilter.done();
		return true;
	}
	return false;
};

export const fetchClientsFromCustomFields = async (searchString: string) => {
	const { Account, AppService } = Tools;
	let clientIds: number[] = [];

	const accountFilter = new RequestBuilder();
	accountFilter.addFilter(Account.attr.active, comparisonTypes.Equals, 1);
	if (!setCustomClientFieldsFilters(accountFilter, searchString)) {
		return [];
	}

	try {
		const response = await Account.customer(AppService.getCustomerId()).find(accountFilter.build());
		clientIds = (response?.data || []).map((client: Client) => client.id);
	} catch (e) {
		logError(e, 'Failed to fetch clients');
	}

	return clientIds;
};

export const getSearchableCustomFields = () => {
	const customFields = (Tools.AppService.getCustomFields('account') || []) as CustomField[];
	return customFields.filter(cf => cf.searchable);
};

export const getMatchingCustomFieldValues = (
	custom: EntityCustomField[] | undefined,
	searchTerm: string,
	searchableFields?: CustomField[]
): { name: string; value: string }[] => {
	if (!custom?.length || !searchTerm) {
		return [];
	}

	const fields = searchableFields ?? getSearchableCustomFields();
	if (!fields.length) {
		return [];
	}

	const searchableFieldIds = new Set(fields.map(f => f.id));
	const fieldNameMap = new Map(fields.map(f => [f.id, f.name]));
	const lowerTerm = searchTerm.toLowerCase();

	return custom
		.filter(cf => searchableFieldIds.has(cf.fieldId) && cf.value && cf.value.toLowerCase().includes(lowerTerm))
		.map(cf => ({
			name: fieldNameMap.get(cf.fieldId) || '',
			value: cf.value as string
		}));
};
